import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, GripVertical, X, Plus } from "lucide-react";
import { Card } from "@/components/ui/card";
import { SearchSeriesBookDialog } from "./SearchSeriesBookDialog";

interface Book {
  id: string;
  book_id: string | null;
  position_in_series: number;
  temp_title?: string;
  temp_isbn13?: string;
  book?: {
    title: string;
    authors: string[];
    cover_url: string | null;
  };
}

interface EditSeriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  series: {
    id: string;
    series_name: string;
    author_name: string;
    series_books: Book[];
  } | null;
  onSaved: () => void;
}

export function EditSeriesDialog({ open, onOpenChange, series, onSaved }: EditSeriesDialogProps) {
  const [books, setBooks] = useState<Book[]>([]);
  const [saving, setSaving] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [addingPosition, setAddingPosition] = useState<number | null>(null);

  // Initialize books when dialog opens
  useEffect(() => {
    if (series && open) {
      setBooks([...series.series_books].sort((a, b) => a.position_in_series - b.position_in_series));
    }
  }, [series, open]);

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newBooks = [...books];
    [newBooks[index - 1], newBooks[index]] = [newBooks[index], newBooks[index - 1]];
    setBooks(newBooks);
  };

  const handleMoveDown = (index: number) => {
    if (index === books.length - 1) return;
    const newBooks = [...books];
    [newBooks[index], newBooks[index + 1]] = [newBooks[index + 1], newBooks[index]];
    setBooks(newBooks);
  };

  const handleRemove = (index: number) => {
    const newBooks = books.filter((_, i) => i !== index);
    setBooks(newBooks);
  };

  const handleAddBook = (position: number) => {
    setAddingPosition(position);
    setSearchDialogOpen(true);
  };

  const handleBookSelected = (bookId: string, bookData: any) => {
    if (addingPosition === null) return;

    const newBook: Book = {
      id: crypto.randomUUID(), // Temporary ID for new books
      book_id: bookId,
      position_in_series: addingPosition,
      book: {
        title: bookData.title,
        authors: bookData.authors || [],
        cover_url: bookData.cover_url,
      },
    };

    const newBooks = [...books];
    newBooks.splice(addingPosition - 1, 0, newBook);
    setBooks(newBooks);
    setSearchDialogOpen(false);
    setAddingPosition(null);
  };

  const handleSave = async () => {
    if (!series) return;

    setSaving(true);
    try {
      // Update all books with series metadata in the books table
      for (let i = 0; i < books.length; i++) {
        const book = books[i];
        if (book.book_id) {
          const { error: updateError } = await supabase
            .from('books')
            .update({
              series_name: series.series_name,
              series_position: i + 1,
              series_total: books.length,
            })
            .eq('id', book.book_id);

          if (updateError) {
            console.error(`Error updating book ${book.book_id}:`, updateError);
          }
        }
      }

      // Delete all existing series_books records
      const { error: deleteError } = await supabase
        .from('series_books')
        .delete()
        .eq('series_suggestion_id', series.id);

      if (deleteError) throw deleteError;

      // Insert all books with their new positions
      const booksToInsert = books
        .filter(book => book.book_id || book.temp_title) // Only insert if we have either a book_id or temp data
        .map((book, index) => ({
          series_suggestion_id: series.id,
          book_id: book.book_id,
          position_in_series: index + 1,
          temp_title: book.temp_title,
          temp_isbn13: book.temp_isbn13,
          is_found: !!book.book_id, // Mark as found if we have a book_id
        }));

      const { error: insertError } = await supabase
        .from('series_books')
        .insert(booksToInsert);

      if (insertError) throw insertError;

      toast.success("Series updated successfully - books now trackable in library");
      onSaved();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error saving series:', error);
      toast.error(`Failed to save: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!series) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Series: {series.series_name}</DialogTitle>
            <DialogDescription>
              by {series.author_name} • Reorder, add, or remove books
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {books.map((book, index) => (
              <Card key={book.id} className="p-4">
                <div className="flex items-center gap-4">
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      className="h-6 px-2"
                    >
                      ▲
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === books.length - 1}
                      className="h-6 px-2"
                    >
                      ▼
                    </Button>
                  </div>

                  <div className="flex items-center justify-center w-12 h-12 rounded bg-primary text-primary-foreground font-bold">
                    {index + 1}
                  </div>

                  {book.book?.cover_url && (
                    <img
                      src={book.book.cover_url}
                      alt={book.book.title}
                      className="w-12 h-16 object-cover rounded"
                    />
                  )}

                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium truncate">
                      {book.book?.title || book.temp_title || 'Unknown Book'}
                    </h4>
                    {book.book?.authors && (
                      <p className="text-sm text-muted-foreground truncate">
                        {book.book.authors.join(', ')}
                      </p>
                    )}
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemove(index)}
                    className="text-destructive hover:text-destructive"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            ))}

            <Button
              variant="outline"
              onClick={() => handleAddBook(books.length + 1)}
              className="w-full"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Book
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SearchSeriesBookDialog
        open={searchDialogOpen}
        onOpenChange={setSearchDialogOpen}
        seriesName={series.series_name}
        authorName={series.author_name}
        position={addingPosition || undefined}
        onBookSelected={handleBookSelected}
      />
    </>
  );
}
